import * as React from 'react';

import ReviewsRatingCard from 'bundles/reviews-common/components/cds/ReviewsRatingCard';
import ReviewsBackButton from 'bundles/reviews/components/ReviewsBackButton';
import ReviewsPageLayout from 'bundles/reviews/components/ReviewsPageLayout';
import ReviewsProductCard from 'bundles/reviews/components/ReviewsProductCard';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import type {
  CourseReviewsPageData,
  CourseReviewsPageSchemaReview,
} from 'bundles/reviews/hooks/useCourseReviewsPageData';
import CourseReviewsPageSeoElements from 'bundles/reviews/pages/course/CourseReviewsPageSeoElements';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

import _t from 'i18n!nls/reviews';

const NUM_TOP_REVIEWS_TO_SHOW = 12;

type Props = {
  reviewsPageData: CourseReviewsPageData;
  schemaReviews: CourseReviewsPageSchemaReview[];
  router: ReviewRouter;
  programSlug?: string;
  orgSlug?: string;
};

const CourseReviewsPageContent = ({
  reviewsPageData,
  schemaReviews,
  router,
  programSlug,
  orgSlug,
}: Props) => {
  const isInUnifiedPageRollout = useIsOrgScopedHome();
  const { id: courseId, name: courseName, slug } = reviewsPageData;
  const productTypeAttributes = reviewsPageData.productCard?.productTypeAttributes;
  const courseCanonical =
    productTypeAttributes?.__typename === 'ProductCard_Course' ? productTypeAttributes.canonical : null;
  const partner = reviewsPageData.partners?.[0];

  let coursePageHref = `/learn/${slug}`;
  if (isInUnifiedPageRollout && orgSlug) {
    coursePageHref = `/organizations/${orgSlug}/learn/${slug}`;
  } else if (programSlug) {
    coursePageHref = `/programs/${programSlug}/learn/${slug}`;
  }

  return (
    <ReviewsPageLayout
      backButton={<ReviewsBackButton courseName={courseName} href={coursePageHref} />}
      productCard={
        <ReviewsProductCard
          courseId={courseId}
          courseName={courseName}
          description={reviewsPageData.description}
          difficultyLevel={reviewsPageData.difficultyLevel}
          imageUrl={courseCanonical?.promoPhotoUrl}
          marketingProductType={reviewsPageData.productCard?.marketingProductType}
          partnerLogoUrl={partner?.squareLogo ?? partner?.logo}
          partnerName={partner?.name}
          programSlug={programSlug}
          skillTags={(reviewsPageData.skillTags ?? []).map((tag) => tag.name)}
          totalDuration={reviewsPageData.material?.totalDuration}
        />
      }
      ratingCard={
        <ReviewsRatingCard
          averageFiveStarRating={reviewsPageData.ratings?.averageFiveStarRating}
          ratingCount={reviewsPageData.ratings?.ratingCount}
          ratingCountsByStar={reviewsPageData.ratingCountsByStar}
        />
      }
      seoElements={
        <CourseReviewsPageSeoElements
          reviewsPageData={reviewsPageData}
          schemaReviews={schemaReviews}
          router={router}
        />
      }
    >
      <TopReviewsSection
        title={_t('Featured reviews')}
        reviews={(reviewsPageData.topReviews ?? [])
          .slice(0, NUM_TOP_REVIEWS_TO_SHOW)
          .map((review) => ({ key: review.id, ...review }))}
      />
      {/* ProductReviewsV1Resource.byProduct's productId is a union field -- courses must be passed
          as a "COURSE~<id>" stringkey, not the bare course id DescriptionPage returns. */}
      <ReviewsTable courseId={`COURSE~${courseId}`} />
    </ReviewsPageLayout>
  );
};

export default CourseReviewsPageContent;
